import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/GradientCard";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Search, PackageCheck, Trash2, Trash, X, PackageOpen, Pencil } from "lucide-react";
import { DispatchModuleAccessButton } from "../components/DispatchAuditControls";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import { useEditAccessRequest } from "../components/EditAccessRequest";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const EMPTY_ISSUE_FORM = { employee_id: "", lines: [], notes: "" };

const STATUS_META = {
  issued: { label: "Issued", chip: "bg-sky-100 text-sky-800" },
  return_requested: { label: "Return Requested", chip: "bg-amber-100 text-amber-800" },
  returned: { label: "Returned", chip: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Rejected", chip: "bg-rose-100 text-rose-800" },
};
const CONDITIONS = ["Working", "Non-Working", "Damaged"];

export default function ProductIssuance() {
  const { user } = useAuth();
  const canIssue = ["master_admin", "dispatch_admin"].includes(user?.role)
    || (user?.granted_modules_edit || []).includes("product_issuance");
  const canApprove = ["master_admin", "it_admin"].includes(user?.role)
    || (user?.granted_modules_edit || []).includes("product_issuance");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inventory] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [issueOpen, setIssueOpen] = useState(false);
  const [issueForm, setIssueForm] = useState(EMPTY_ISSUE_FORM);
  const [saving, setSaving] = useState(false);

  // approveTarget: the issuance being returned; approveLines holds per-line decisions
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveLines, setApproveLines] = useState([]);
  const [approveGlobalRemark, setApproveGlobalRemark] = useState("");

  // ---- Issuance request approvals (employee-raised requests for materials) ----
  const [issuanceRequests, setIssuanceRequests] = useState([]);
  // v109.8 — Dispatch audit: mandatory reason on delete
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [viewMode] = useGlobalViewMode();
  // v109.9.4 — Edit issued products (within 1 day window; else request access)
  const { dialog: editReqDialog, wrapMutation: wrapEditRequest } = useEditAccessRequest();
  const [editIss, setEditIss] = useState(null);      // {id, issuance_no, items, notes}
  const [editIssReason, setEditIssReason] = useState("");
  const [editIssBusy, setEditIssBusy] = useState(false);
  const loadIssuanceRequests = () => {
    if (!canApprove && !canIssue) return;
    api.get("/issuance-requests")
      .then(({ data }) => setIssuanceRequests((data || []).filter((r) => r.status === "pending")))
      .catch(() => setIssuanceRequests([]));
  };

  const load = () => {
    setLoading(true);
    api.get("/product-issuances")
      .then(({ data }) => setItems(data || []))
      .catch((err) => toast.error(formatError(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    loadIssuanceRequests();
    api.get("/directory").then(({ data }) => setEmployees(data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const approveIssuanceRequest = async (id) => {
    try {
      const { data } = await api.post(`/issuance-requests/${id}/approve`);
      toast.success(`Issuance ${data.issuance_no || ""} created`);
      load();
      loadIssuanceRequests();
    } catch (err) { toast.error(formatError(err)); }
  };
  const rejectIssuanceRequest = async (id) => {
    if (!window.confirm("Reject this material request?")) return;
    try {
      await api.post(`/issuance-requests/${id}/reject`, { remark: "Rejected" });
      toast.success("Request rejected");
      loadIssuanceRequests();
    } catch (err) { toast.error(formatError(err)); }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return items.filter((i) => {
      if (statusFilter !== "all" && i.status !== statusFilter) return false;
      if (!q) return true;
      const itemNames = (i.items || []).map((it) => `${it.inventory_name} ${it.inventory_sku}`).join(" ");
      return [i.issuance_no, itemNames, i.employee_name, i.employee_code]
        .some((v) => String(v ?? "").toLowerCase().includes(q));
    });
  }, [items, search, statusFilter]);

  const counts = useMemo(() => {
    const c = { all: items.length, issued: 0, return_requested: 0, returned: 0, rejected: 0 };
    items.forEach((i) => { if (c[i.status] != null) c[i.status] += 1; });
    return c;
  }, [items]);

  // ---- Issue dialog ----
  const removeLine = (idx) => setIssueForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));
  const updateLine = (idx, patch) => setIssueForm((f) => ({
    ...f,
    lines: f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
  }));

  const submitIssue = async () => {
    if (!issueForm.employee_id) { toast.error("Select an employee"); return; }
    // A line is valid if it has EITHER inventory_id OR custom_name, plus qty > 0
    const validLines = issueForm.lines.filter((l) =>
      (l.inventory_id || (l.custom_name && l.custom_name.trim())) && Number(l.qty_issued) > 0
    );
    if (validLines.length === 0) { toast.error("Add at least one product line with quantity"); return; }
    // Dedupe inventory IDs across lines (frontend safety; backend allows duplicates anyway).
    const seenInv = new Set();
    for (const l of validLines) {
      if (l.inventory_id) {
        if (seenInv.has(l.inventory_id)) {
          toast.error("Duplicate inventory item — pick distinct items");
          return;
        }
        seenInv.add(l.inventory_id);
      }
    }
    setSaving(true);
    try {
      await api.post("/product-issuances", {
        employee_id: issueForm.employee_id,
        items: validLines.map((l) => l.inventory_id
          ? { inventory_id: l.inventory_id, qty_issued: Number(l.qty_issued) }
          : { custom_name: l.custom_name.trim(), qty_issued: Number(l.qty_issued) }
        ),
        notes: issueForm.notes,
      });
      toast.success(`${validLines.length} material(s) issued`);
      setIssueOpen(false);
      setIssueForm(EMPTY_ISSUE_FORM);
      load();
      // Refresh inventory so decremented qty is reflected for next issuance
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  // ---- Approve return dialog ----
  const openApprove = (issuance) => {
    setApproveTarget(issuance);
    setApproveLines((issuance.items || []).map((line) => ({
      line_id: line.line_id,
      inventory_name: line.inventory_name,
      inventory_sku: line.inventory_sku,
      qty_issued: line.qty_issued,
      return_condition: "Working",
      deduct_cost: false,
      deduct_amount: 0,
      it_remark: "",
    })));
    setApproveGlobalRemark("");
  };

  const updateApproveLine = (idx, patch) => setApproveLines((prev) =>
    prev.map((l, i) => (i === idx ? { ...l, ...patch } : l))
  );

  const submitApprove = async () => {
    if (!approveTarget) return;
    // Validate every line has a condition; deduct lines have amount > 0
    for (const l of approveLines) {
      if (!CONDITIONS.includes(l.return_condition)) {
        toast.error(`Set condition for ${l.inventory_name}`); return;
      }
      if (l.deduct_cost && !(Number(l.deduct_amount) > 0)) {
        toast.error(`Enter deduction amount for ${l.inventory_name}`); return;
      }
    }
    setSaving(true);
    try {
      await api.post(`/product-issuances/${approveTarget.id}/approve-return`, {
        items: approveLines.map((l) => ({
          line_id: l.line_id,
          return_condition: l.return_condition,
          deduct_cost: !!l.deduct_cost,
          deduct_amount: Number(l.deduct_amount) || 0,
          it_remark: l.it_remark || "",
        })),
        it_remark: approveGlobalRemark,
      });
      toast.success("Return approved");
      setApproveTarget(null);
      setApproveLines([]);
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const rejectReturn = async (id) => {
    try {
      await api.post(`/product-issuances/${id}/reject-return`, { it_remark: "Return rejected by IT" });
      toast.success("Return rejected — item back to Issued");
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const deleteIssuance = (id) => {
    setDeleteTarget(id);
    setDeleteReason("");
  };

  // v109.9.4 — Edit issued products (items + notes). 1-day direct window,
  // else backend returns 409 → useEditAccessRequest auto-opens request dialog.
  const openEditIssuance = (iss) => {
    setEditIss({
      id: iss.id,
      issuance_no: iss.issuance_no,
      notes: iss.notes || "",
      items: (iss.items || []).map((li) => ({ ...li })),
    });
    setEditIssReason("");
  };

  const submitEditIssuance = async () => {
    if (!editIss) return;
    if (editIssReason.trim().length < 3) { toast.error("Reason (min 3 chars) required"); return; }
    setEditIssBusy(true);
    try {
      const payload = {
        change_reason: editIssReason.trim(),
        notes: editIss.notes,
        items: (editIss.items || [])
          .filter((li) => Number(li.qty_issued) > 0)
          .map((li) => ({
            line_id: li.line_id,
            inventory_id: li.inventory_id || "",
            inventory_name: li.inventory_name || li.custom_name || "",
            inventory_sku: li.inventory_sku || "",
            custom_name: li.custom_name || "",
            qty_issued: Number(li.qty_issued),
          })),
      };
      const result = await wrapEditRequest(
        () => api.patch(`/product-issuances/${editIss.id}`, payload),
        { moduleKey: "product_issuance", recordId: editIss.id, recordLabel: editIss.issuance_no, proposedPayload: payload },
      );
      if (!result) {
        // v109.9.5 — staged for approval
        setEditIss(null);
        setEditIssReason("");
        setEditIssBusy(false);
        return;
      }
      toast.success("Issuance updated");
      setEditIss(null);
      setEditIssReason("");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setEditIssBusy(false); }
  };

  const submitDelete = async () => {
    if (!deleteTarget) return;
    if (deleteReason.trim().length < 3) {
      toast.error("Please state a reason for deletion (min 3 chars)");
      return;
    }
    try {
      await api.delete(`/product-issuances/${deleteTarget}`, {
        data: { delete_reason: deleteReason.trim() },
      });
      toast.success("Moved to Recycle Bin — purged after 30 days");
      setDeleteTarget(null);
      setDeleteReason("");
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  // Total deduction for an issuance (sum across all its items)
  const totalDeduction = (iss) => (iss.items || [])
    .filter((it) => it.deduct_cost)
    .reduce((s, it) => s + Number(it.deduct_amount || 0), 0);

  return (
    <div className="max-w-[1400px] space-y-6">
      <PageHeader
        moduleKey="product_issuance"
        tone="violet"
        icon={PackageOpen}
        eyebrow="Workspace"
        title="Product Issuance"
        subtitle="Issue multiple items to an employee in one go. IT marks per-item condition and cost deduction on return."
        actions={
          <div className="flex items-center gap-2">
            <DispatchModuleAccessButton moduleKey="product_issuance" label="Product Issuance" />
            {canIssue && (
              <Button onClick={() => { setIssueForm({ ...EMPTY_ISSUE_FORM, lines: [] }); setIssueOpen(true); }} data-testid="issuance-new-btn">
                <Plus size={14} className="mr-1" /> Issue Material
              </Button>
            )}
          </div>
        }
      />

      {(canApprove || canIssue) && issuanceRequests.length > 0 && (
        <div className="pk-card overflow-hidden border-l-4 border-sky-400" data-testid="issuance-requests-section">
          <div className="px-5 py-3 bg-sky-50/60 border-b border-sky-100 flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-sky-800 flex items-center gap-2">
                <PackageCheck size={14} /> Material Requests · Pending Approval
              </div>
              <div className="text-[12px] text-sky-700">Approving a request auto-creates a Product Issuance (ISS-xxxx) for the requester.</div>
            </div>
            <Badge className="bg-sky-600 text-white">{issuanceRequests.length} pending</Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60 border-b border-slate-100">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2.5 font-semibold">Requester</th>
                  <th className="px-4 py-2.5 font-semibold">Project · Reason</th>
                  <th className="px-4 py-2.5 font-semibold">Items</th>
                  <th className="px-4 py-2.5 font-semibold">Priority</th>
                  <th className="px-4 py-2.5 font-semibold">Required By</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {issuanceRequests.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0" data-testid={`iss-req-row-${r.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-semibold text-slate-800">{r.requester_name || "—"}</div>
                      <div className="text-[10px] text-slate-400 mono">
                        {r.created_at ? new Date(r.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-[12px] max-w-xs">
                      <div className="font-medium text-slate-700">{r.project_name || "—"}</div>
                      <div className="text-slate-500 truncate" title={r.reason}>{r.reason || "—"}</div>
                    </td>
                    <td className="px-4 py-2.5 text-[12px]">
                      <ul className="space-y-0.5">
                        {(r.items || []).map((it, i) => (
                          <li key={i} className="flex justify-between gap-2">
                            <span className="truncate">{it.inventory_name}</span>
                            <span className="mono font-semibold flex-shrink-0">×{it.qty}</span>
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge className={
                        r.priority === "Urgent" ? "bg-rose-100 text-rose-800" :
                        r.priority === "High" ? "bg-amber-100 text-amber-800" :
                        r.priority === "Low" ? "bg-slate-100 text-slate-700" :
                        "bg-sky-100 text-sky-800"
                      }>{r.priority}</Badge>
                    </td>
                    <td className="px-4 py-2.5 mono text-[11px] text-slate-600">{r.required_by || "—"}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      {canApprove && (
                        <>
                          <Button size="sm" onClick={() => approveIssuanceRequest(r.id)} className="bg-emerald-600 hover:bg-emerald-700 h-8 text-[11px] mr-1.5" data-testid={`iss-req-approve-${r.id}`}>
                            Approve & Issue
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => rejectIssuanceRequest(r.id)} className="text-rose-700 border-rose-200 hover:bg-rose-50 h-8 text-[11px]" data-testid={`iss-req-reject-${r.id}`}>
                            Reject
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-md flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search issuance no, product, employee…"
            className="pl-9 h-10"
            data-testid="issuance-search"
          />
        </div>
        <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-auto">
          <TabsList>
            {[
              { key: "all", label: "All" },
              { key: "issued", label: "Issued" },
              { key: "return_requested", label: "Return Requested" },
              { key: "returned", label: "Returned" },
              { key: "rejected", label: "Rejected" },
            ].map((s) => (
              <TabsTrigger key={s.key} value={s.key} data-testid={`issuance-filter-${s.key}`}>
                {s.label} · {counts[s.key] || 0}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {loading ? (
        <div className="pk-card p-12 text-center text-slate-400">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="pk-card p-12 text-center text-slate-400" data-testid="issuance-empty">
          No issuances {statusFilter !== "all" ? `in '${statusFilter}'` : "yet"}.
        </div>
      ) : viewMode === "list" ? (
        <div className="pk-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm responsive-table" data-testid="issuance-table">
              <thead className="bg-slate-50/80 border-b">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 font-semibold">Issuance #</th>
                  <th className="px-4 py-3 font-semibold">Employee</th>
                  <th className="px-4 py-3 font-semibold">Items</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Deduction</th>
                  <th className="px-4 py-3 font-semibold">Issued</th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((iss) => {
                  const meta = STATUS_META[iss.status] || {};
                  const deduction = totalDeduction(iss);
                  return (
                    <tr key={iss.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50" data-testid={`issuance-row-${iss.id}`}>
                      <td className="px-4 py-2.5 mono font-bold text-slate-800" data-label="Issuance #">{iss.issuance_no}</td>
                      <td className="px-4 py-2.5" data-label="Employee">
                        <div className="font-semibold text-slate-800 text-[12.5px]">{iss.employee_name}</div>
                        {iss.employee_code && <div className="text-[10px] mono text-slate-400">{iss.employee_code}</div>}
                      </td>
                      <td className="px-4 py-2.5 text-[11.5px]" data-label="Items">
                        <div className="flex flex-wrap gap-1 max-w-[300px]">
                          {(iss.items || []).slice(0, 3).map((line) => (
                            <Badge key={line.line_id} variant="secondary" className="text-[10px]">
                              <span className="truncate max-w-[110px]">{line.inventory_name}</span>
                              <span className="ml-1 mono font-bold">×{line.qty_issued}</span>
                            </Badge>
                          ))}
                          {(iss.items || []).length > 3 && <span className="text-[10px] text-slate-500">+{iss.items.length - 3}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-2.5" data-label="Status">
                        <Badge className={`${meta.chip || "bg-slate-100 text-slate-700"} text-[10px]`}>{meta.label || iss.status}</Badge>
                      </td>
                      <td className="px-4 py-2.5" data-label="Deduction">
                        {deduction > 0
                          ? <span className="mono text-[11px] font-semibold text-rose-700">₹ {deduction.toLocaleString("en-IN")}</span>
                          : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-[11px] mono text-slate-500" data-label="Issued">{iss.issued_at ? new Date(iss.issued_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</td>
                      <td className="px-4 py-2.5 text-right" data-label="Actions">
                        <div className="inline-flex items-center gap-1.5">
                          {canApprove && iss.status === "return_requested" && (
                            <>
                              <Button size="sm" onClick={() => openApprove(iss)} className="bg-emerald-600 hover:bg-emerald-700 h-7 text-[10.5px]" data-testid={`issuance-row-approve-${iss.id}`}>
                                Approve
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => rejectReturn(iss.id)} className="h-7 text-[10.5px] text-rose-700 border-rose-200 hover:bg-rose-50" data-testid={`issuance-row-reject-${iss.id}`}>
                                Reject
                              </Button>
                            </>
                          )}
                          {canIssue && iss.status !== "returned" && (
                            <>
                              <Button size="sm" variant="outline" className="h-7 text-[10.5px] text-indigo-700 border-indigo-200 hover:bg-indigo-50" onClick={() => openEditIssuance(iss)} data-testid={`issuance-row-edit-${iss.id}`}>
                                <Pencil size={11} className="mr-1" /> Edit
                              </Button>
                              <Button size="sm" variant="outline" className="h-7 text-[10.5px] text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => deleteIssuance(iss.id)} data-testid={`issuance-row-delete-${iss.id}`}>
                                <Trash2 size={11} />
                              </Button>
                            </>
                          )}
                          {!canIssue && !canApprove && (
                            <Badge className="bg-slate-100 text-slate-500 text-[9.5px] ring-1 ring-slate-200">View only</Badge>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="issuance-grid">
          {filtered.map((iss) => {
            const meta = STATUS_META[iss.status] || {};
            const deduction = totalDeduction(iss);
            return (
              <div key={iss.id} className="pk-card p-4" data-testid={`issuance-card-${iss.id}`}>
                <div className="flex items-start justify-between mb-2 gap-3">
                  <div>
                    <div className="mono text-[12px] font-bold text-slate-800">{iss.issuance_no}</div>
                    <div className="text-[11px] text-slate-500">
                      Issued to <span className="text-slate-700 font-medium">{iss.employee_name}</span>
                      {iss.employee_code && <span className="mono"> · {iss.employee_code}</span>}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      {iss.issued_at ? new Date(iss.issued_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge className={`${meta.chip || "bg-slate-100 text-slate-700"} hover:bg-inherit`}>
                      {meta.label || iss.status}
                    </Badge>
                    {deduction > 0 && (
                      <div className="mono text-[11px] font-semibold text-rose-700 mt-1">₹ {deduction.toLocaleString("en-IN")} deducted</div>
                    )}
                  </div>
                </div>

                <table className="w-full text-[12px] mt-2">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b">
                      <th className="py-1.5 pr-2 font-semibold">Product</th>
                      <th className="py-1.5 px-2 font-semibold text-right">Qty</th>
                      <th className="py-1.5 pl-2 font-semibold text-right">Return State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(iss.items || []).map((line) => (
                      <tr key={line.line_id} className="border-b last:border-0">
                        <td className="py-1.5 pr-2">
                          <div className="font-medium text-slate-800">{line.inventory_name}</div>
                          {line.inventory_sku && <div className="mono text-[10px] text-slate-500">{line.inventory_sku}</div>}
                        </td>
                        <td className="py-1.5 px-2 text-right mono font-semibold text-slate-800">{line.qty_issued}</td>
                        <td className="py-1.5 pl-2 text-right">
                          {iss.status === "returned" && line.return_condition ? (
                            <div>
                              <Badge className={
                                line.return_condition === "Damaged" ? "bg-rose-100 text-rose-800" :
                                line.return_condition === "Non-Working" ? "bg-amber-100 text-amber-800" :
                                "bg-emerald-100 text-emerald-800"
                              }>
                                {line.return_condition}
                              </Badge>
                              {line.deduct_cost && (
                                <div className="mono text-[11px] text-rose-700 font-semibold mt-0.5">−₹ {Number(line.deduct_amount).toLocaleString("en-IN")}</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400 text-[11px]">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {iss.notes && (
                  <div className="mt-2 text-[11px] text-slate-500"><span className="font-semibold">Note:</span> {iss.notes}</div>
                )}
                {iss.return_remark && iss.status !== "returned" && (
                  <div className="mt-2 text-[11px] text-amber-700"><span className="font-semibold">Employee remark:</span> {iss.return_remark}</div>
                )}

                <div className="mt-3 flex items-center justify-end gap-1.5">
                  {canApprove && iss.status === "return_requested" && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => openApprove(iss)}
                        data-testid={`issuance-approve-${iss.id}`}
                        className="bg-emerald-600 hover:bg-emerald-700 h-7 text-[11px]"
                      >
                        <PackageCheck size={12} className="mr-1" /> Approve Return
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => rejectReturn(iss.id)}
                        data-testid={`issuance-reject-${iss.id}`}
                        className="h-7 text-[11px] text-rose-700 border-rose-200 hover:bg-rose-50"
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  {user?.role === "master_admin" && (
                    <button
                      onClick={() => deleteIssuance(iss.id)}
                      className="h-7 w-7 inline-flex items-center justify-center text-rose-600 hover:text-rose-700 rounded hover:bg-rose-50"
                      data-testid={`issuance-delete-${iss.id}`}
                      title="Delete"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                  {canIssue && iss.status !== "returned" && (
                    <Button size="sm" variant="outline" className="h-7 text-[10.5px] text-indigo-700 border-indigo-200 hover:bg-indigo-50" onClick={() => openEditIssuance(iss)} data-testid={`issuance-card-edit-${iss.id}`}>
                      <Pencil size={11} className="mr-1" /> Edit
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Issue Material Dialog */}
      <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Issue Material to Employee</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Issue To Employee <span className="text-rose-500">*</span></Label>
              <Select value={issueForm.employee_id} onValueChange={(v) => setIssueForm({ ...issueForm, employee_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="issuance-form-employee">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id} data-testid={`issuance-emp-opt-${e.id}`}>
                      {e.name}{e.employee_code ? ` · ${e.employee_code}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                Products & Quantities <span className="text-rose-500">*</span>
              </Label>
              <div className="text-[11px] text-slate-500 mt-0.5 mb-1.5">
                Search and pick multiple items from Inventory, or type any product name to add as custom. Set qty per item below.
              </div>

              <MultiProductPicker
                inventory={inventory}
                selectedLines={issueForm.lines.filter((l) => l.inventory_id || l.custom_name)}
                onAddInventory={(item) => {
                  // Append only if not already present
                  const exists = issueForm.lines.some((l) => l.inventory_id === item.id);
                  if (exists) { toast.error(`${item.product_name} already added`); return; }
                  const newLine = {
                    inventory_id: item.id,
                    inventory_label: `${item.product_name}${item.sku ? ` · ${item.sku}` : ""}`,
                    custom_name: "",
                    qty_issued: 1,
                  };
                  setIssueForm((f) => ({
                    ...f,
                    lines: [...f.lines.filter((l) => l.inventory_id || l.custom_name), newLine],
                  }));
                }}
                onAddCustom={(name) => {
                  const exists = issueForm.lines.some(
                    (l) => l.custom_name && l.custom_name.toLowerCase() === name.toLowerCase()
                  );
                  if (exists) { toast.error("Already added"); return; }
                  const newLine = { inventory_id: "", inventory_label: "", custom_name: name, qty_issued: 1 };
                  setIssueForm((f) => ({
                    ...f,
                    lines: [...f.lines.filter((l) => l.inventory_id || l.custom_name), newLine],
                  }));
                }}
              />

              {/* Selected items with qty editors */}
              {issueForm.lines.filter((l) => l.inventory_id || l.custom_name).length === 0 ? (
                <div className="mt-3 p-4 border border-dashed border-slate-200 rounded-md text-center text-[12px] text-slate-400">
                  No products selected yet.
                </div>
              ) : (
                <div className="mt-3 space-y-1.5">
                  {issueForm.lines.map((line, idx) => {
                    if (!line.inventory_id && !line.custom_name) return null;
                    const isCustom = !line.inventory_id && !!line.custom_name;
                    const label = line.inventory_label || line.custom_name || "—";
                    return (
                      <div
                        key={idx}
                        className="flex items-center gap-2 p-2 pl-3 rounded-md border bg-slate-50/60"
                        data-testid={`issuance-line-${idx}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-medium text-slate-800 truncate" data-testid={`issuance-line-label-${idx}`}>
                            {label}
                          </div>
                          {isCustom && (
                            <span className="inline-block text-[9px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded mt-0.5">
                              Custom
                            </span>
                          )}
                        </div>
                        <Input
                          type="number"
                          min={1}
                          step={1}
                          placeholder="Qty"
                          value={line.qty_issued}
                          onChange={(e) => updateLine(idx, { qty_issued: e.target.value })}
                          className="w-24 h-9 text-center mono"
                          data-testid={`issuance-line-qty-${idx}`}
                        />
                        <button
                          type="button"
                          onClick={() => removeLine(idx)}
                          className="h-9 w-9 inline-flex items-center justify-center text-rose-600 hover:bg-rose-50 rounded"
                          data-testid={`issuance-line-rm-${idx}`}
                          title="Remove"
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Notes (optional)</Label>
              <Textarea
                value={issueForm.notes}
                onChange={(e) => setIssueForm({ ...issueForm, notes: e.target.value })}
                rows={2}
                className="mt-1.5"
                data-testid="issuance-form-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIssueOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={submitIssue} disabled={saving} data-testid="issuance-form-save">
              {saving ? "Issuing…" : "Issue Material"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* IT Admin Approve Return Dialog — per-item condition + cost */}
      <Dialog open={!!approveTarget} onOpenChange={(o) => { if (!o) { setApproveTarget(null); setApproveLines([]); } }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          {approveTarget && (
            <>
              <DialogHeader>
                <DialogTitle>Approve Return — {approveTarget.issuance_no}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded border text-[12px]">
                  <div className="text-slate-600"><span className="font-semibold">Employee:</span> {approveTarget.employee_name}</div>
                  {approveTarget.return_remark && (
                    <div className="text-slate-600 mt-1"><span className="font-semibold">Employee remark:</span> {approveTarget.return_remark}</div>
                  )}
                </div>

                <div>
                  <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-2 block">
                    Per-Item Condition & Deduction
                  </Label>
                  <div className="space-y-3">
                    {approveLines.map((line, idx) => (
                      <div key={line.line_id} className="p-3 rounded-md border bg-white" data-testid={`approve-line-${idx}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="font-semibold text-slate-800 text-sm">{line.inventory_name}</div>
                            <div className="text-[11px] text-slate-500 mono">
                              {line.inventory_sku && <span>{line.inventory_sku} · </span>}Qty {line.qty_issued}
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Condition</Label>
                            <Select
                              value={line.return_condition}
                              onValueChange={(v) => updateApproveLine(idx, { return_condition: v })}
                            >
                              <SelectTrigger className="mt-1" data-testid={`approve-line-cond-${idx}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {CONDITIONS.map((c) => (
                                  <SelectItem key={c} value={c}>{c}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Deduct Cost?</Label>
                            <div className="mt-1 flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => updateApproveLine(idx, { deduct_cost: true })}
                                className={`flex-1 h-9 text-[11px] font-semibold rounded border ${line.deduct_cost ? "bg-rose-100 border-rose-300 text-rose-800" : "bg-white border-slate-200 text-slate-600"}`}
                                data-testid={`approve-line-deduct-yes-${idx}`}
                              >
                                Yes
                              </button>
                              <button
                                type="button"
                                onClick={() => updateApproveLine(idx, { deduct_cost: false, deduct_amount: 0 })}
                                className={`flex-1 h-9 text-[11px] font-semibold rounded border ${!line.deduct_cost ? "bg-slate-200 border-slate-300 text-slate-700" : "bg-white border-slate-200 text-slate-600"}`}
                                data-testid={`approve-line-deduct-no-${idx}`}
                              >
                                No
                              </button>
                            </div>
                          </div>
                          <div>
                            <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Amount (INR)</Label>
                            <Input
                              type="number"
                              min={0}
                              step="0.01"
                              disabled={!line.deduct_cost}
                              value={line.deduct_amount}
                              onChange={(e) => updateApproveLine(idx, { deduct_amount: e.target.value })}
                              className="mt-1 mono"
                              data-testid={`approve-line-amount-${idx}`}
                            />
                          </div>
                        </div>

                        <div className="mt-2">
                          <Input
                            placeholder="Remark for this item (optional)"
                            value={line.it_remark}
                            onChange={(e) => updateApproveLine(idx, { it_remark: e.target.value })}
                            className="text-[12px]"
                            data-testid={`approve-line-remark-${idx}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Team Remark (optional)</Label>
                  <Textarea
                    value={approveGlobalRemark}
                    onChange={(e) => setApproveGlobalRemark(e.target.value)}
                    rows={2}
                    className="mt-1.5"
                    data-testid="approve-global-remark"
                  />
                </div>

                <div className="p-3 bg-rose-50 border border-rose-200 rounded flex items-center justify-between" data-testid="approve-total">
                  <span className="text-[11px] uppercase tracking-wider font-semibold text-rose-700">Total Deduction</span>
                  <span className="mono text-base font-bold text-rose-800">
                    ₹ {approveLines.filter((l) => l.deduct_cost).reduce((s, l) => s + (Number(l.deduct_amount) || 0), 0).toLocaleString("en-IN")}
                  </span>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setApproveTarget(null); setApproveLines([]); }} disabled={saving}>Cancel</Button>
                <Button onClick={submitApprove} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-save">
                  {saving ? "Saving…" : "Approve Return"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* v109.8 — Delete confirmation with mandatory reason */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setDeleteReason(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete issuance record?</AlertDialogTitle>
            <AlertDialogDescription>
              The record will be soft-deleted and <b>permanently purged after 30 days</b>. Master Admin can restore it from Dispatch → Logs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-3">
            <Label className="text-[11px] uppercase tracking-wider font-bold text-rose-700 mb-1">
              Reason (mandatory) <span className="text-rose-500">*</span>
            </Label>
            <Textarea
              rows={2}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value.slice(0, 500))}
              placeholder="e.g. Duplicate issuance / wrong employee / cancelled…"
              className="text-[12.5px]"
              data-testid="issuance-delete-reason"
              autoFocus
            />
            <div className="text-[10px] text-slate-400 flex justify-between mt-1">
              <span>{deleteReason.trim().length < 3 ? "Enter at least 3 characters" : "Looks good"}</span>
              <span>{deleteReason.length}/500</span>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={submitDelete}
              disabled={deleteReason.trim().length < 3}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="issuance-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* v109.9.4 — Edit Issuance dialog (within 1-day window; else request access) */}
      <Dialog open={!!editIss} onOpenChange={(o) => { if (!o && !editIssBusy) { setEditIss(null); setEditIssReason(""); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="issuance-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil size={16} className="text-indigo-600" /> Edit Issuance {editIss?.issuance_no || ""}
            </DialogTitle>
          </DialogHeader>
          {editIss && (
            <div className="space-y-3">
              <div className="rounded-md border border-indigo-200 bg-indigo-50/60 px-3 py-2 text-[11px] text-indigo-800">
                Direct edit allowed within <b>1 day</b> of issuance. After that, click Save to raise an access request to Master Admin.
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Notes</Label>
                <Textarea rows={2} value={editIss.notes} onChange={(e) => setEditIss({ ...editIss, notes: e.target.value })} className="mt-1" data-testid="issuance-edit-notes" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Items — adjust quantity</Label>
                <div className="space-y-1.5 mt-1 border rounded-md p-2">
                  {(editIss.items || []).map((li, i) => (
                    <div key={li.line_id || i} className="grid grid-cols-12 gap-1.5 items-center">
                      <div className="col-span-8 text-[12px]">
                        <div className="font-medium">{li.inventory_name || li.custom_name || "—"}</div>
                        {li.inventory_sku && <div className="text-[10px] mono text-slate-500">{li.inventory_sku}</div>}
                      </div>
                      <Input
                        type="number" min="0" step="1"
                        value={li.qty_issued}
                        onChange={(e) => {
                          const items = [...editIss.items];
                          items[i] = { ...items[i], qty_issued: e.target.value };
                          setEditIss({ ...editIss, items });
                        }}
                        className="col-span-4 h-8 text-[12px] mono text-center"
                        data-testid={`issuance-edit-qty-${i}`}
                      />
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1.5">
                  Inventory stock is auto-adjusted by the qty delta on save.
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider font-bold text-rose-700">Reason for this change <span className="text-rose-500">*</span></Label>
                <Textarea
                  rows={2}
                  value={editIssReason}
                  onChange={(e) => setEditIssReason(e.target.value.slice(0, 500))}
                  placeholder="e.g. Adjusted qty as extra unit returned separately…"
                  className="text-[12.5px] mt-1"
                  data-testid="issuance-edit-reason"
                />
                <div className="text-[10px] text-slate-400 flex justify-between mt-1">
                  <span>{editIssReason.trim().length < 3 ? "Min 3 characters" : "Looks good"}</span>
                  <span>{editIssReason.length}/500</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditIss(null); setEditIssReason(""); }} disabled={editIssBusy}>Cancel</Button>
            <Button onClick={submitEditIssuance} disabled={editIssBusy || editIssReason.trim().length < 3} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="issuance-edit-submit">
              {editIssBusy ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v109.9.4 — auto-opens if backend returned 409 edit_window_expired */}
      {editReqDialog}
    </div>
  );
}

function MultiProductPicker({ inventory, selectedLines, onAddInventory, onAddCustom }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selectedInventoryIds = new Set(
    (selectedLines || []).filter((l) => l.inventory_id).map((l) => l.inventory_id)
  );
  const selectedCustomLower = new Set(
    (selectedLines || []).filter((l) => !l.inventory_id && l.custom_name).map((l) => l.custom_name.toLowerCase())
  );

  // Inventory suggestions matching the query (skip already-selected items).
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = inventory.filter((i) => !selectedInventoryIds.has(i.id));
    if (!q) return pool.slice(0, 10);
    return pool
      .filter((i) => `${i.product_name} ${i.sku || ""}`.toLowerCase().includes(q))
      .slice(0, 10);
  }, [inventory, query, selectedLines]); // eslint-disable-line react-hooks/exhaustive-deps

  const trimmed = query.trim();
  const exactInventoryMatch = trimmed
    ? inventory.find((i) => i.product_name.toLowerCase() === trimmed.toLowerCase())
    : null;
  const canAddCustom = trimmed.length > 0
    && !exactInventoryMatch
    && !selectedCustomLower.has(trimmed.toLowerCase());

  const handlePickInventory = (item) => {
    onAddInventory(item);
    setQuery("");
    // keep open so user can keep adding
  };

  const handleAddCustom = () => {
    if (!canAddCustom) return;
    onAddCustom(trimmed);
    setQuery("");
  };

  return (
    <div className="relative">
      <Input
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (suggestions.length > 0) handlePickInventory(suggestions[0]);
            else if (canAddCustom) handleAddCustom();
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        placeholder="Search Inventory or type to add custom…"
        className="h-10"
        data-testid="issuance-multi-picker-input"
      />

      {open && (suggestions.length > 0 || canAddCustom) && (
        <div
          className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-y-auto"
          data-testid="issuance-multi-picker-suggestions"
        >
          {suggestions.length > 0 && (
            <div>
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-50/60">
                From Inventory
              </div>
              {suggestions.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handlePickInventory(item)}
                  className="w-full px-3 py-2 text-left text-[12px] hover:bg-violet-50 flex items-center justify-between gap-2 border-b last:border-0"
                  data-testid={`issuance-multi-picker-opt-${item.id}`}
                >
                  <span className="text-slate-800 font-medium truncate">
                    {item.product_name}
                    {item.quantity !== undefined && (
                      <span className="ml-1.5 text-[10px] mono text-slate-500 font-normal">
                        (avail: {item.quantity})
                      </span>
                    )}
                  </span>
                  {item.sku && <span className="mono text-[10px] text-slate-500 flex-shrink-0">{item.sku}</span>}
                </button>
              ))}
            </div>
          )}
          {canAddCustom && (
            <div>
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-50/60">
                Custom (not in Inventory)
              </div>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleAddCustom}
                className="w-full px-3 py-2 text-left text-[12px] hover:bg-amber-50 flex items-center gap-2"
                data-testid="issuance-multi-picker-add-custom"
              >
                <Plus size={12} className="text-amber-600" />
                <span className="text-slate-800">Add as custom: <span className="font-semibold">"{trimmed}"</span></span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function ProductPicker({ inventory, value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  // displayText is what the user sees in the input. It is independent from the
  // selection itself, allowing free-text typing without losing the chosen item.
  const [query, setQuery] = useState(() => value?.inventory_label || value?.custom_name || "");

  // Sync down: when parent value resets (e.g. dialog reopened), reset query too
  useEffect(() => {
    setQuery(value?.inventory_label || value?.custom_name || "");
  }, [value?.line_id, value?.inventory_id, value?.custom_name]); // eslint-disable-line react-hooks/exhaustive-deps

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return inventory.slice(0, 8);  // show first few on focus
    return inventory
      .filter((i) => `${i.product_name} ${i.sku || ""}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [inventory, query]);

  const trimmed = query.trim();
  const exactMatch = trimmed
    ? inventory.find((i) => i.product_name.toLowerCase() === trimmed.toLowerCase())
    : null;
  const canAddCustom = trimmed.length > 0 && !exactMatch;

  const pickInventory = (item) => {
    onChange({
      inventory_id: item.id,
      inventory_label: `${item.product_name}${item.sku ? ` · ${item.sku}` : ""}`,
      custom_name: "",
    });
    setQuery(`${item.product_name}${item.sku ? ` · ${item.sku}` : ""}`);
    setOpen(false);
  };

  const acceptCustom = () => {
    if (!canAddCustom) return;
    onChange({
      inventory_id: "",
      inventory_label: "",
      custom_name: trimmed,
    });
    setQuery(trimmed);
    setOpen(false);
  };

  const clear = () => {
    onChange({ inventory_id: "", inventory_label: "", custom_name: "" });
    setQuery("");
  };

  const isCustomSelection = !value?.inventory_id && !!value?.custom_name;

  return (
    <div className="relative">
      <Input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          // Clear any prior selection when user starts editing again
          if (value?.inventory_id || value?.custom_name) {
            onChange({ inventory_id: "", inventory_label: "", custom_name: "" });
          }
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (suggestions.length > 0) pickInventory(suggestions[0]);
            else if (canAddCustom) acceptCustom();
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        placeholder="Type to search or add product…"
        className="pr-7"
        data-testid={`${testid}-input`}
      />
      {(value?.inventory_id || value?.custom_name) && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={clear}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-rose-600"
          data-testid={`${testid}-clear`}
        >
          <X size={12} />
        </button>
      )}
      {isCustomSelection && (
        <span className="absolute right-7 top-1/2 -translate-y-1/2 text-[9px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 px-1 rounded">
          Custom
        </span>
      )}

      {open && (suggestions.length > 0 || canAddCustom) && (
        <div
          className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-y-auto"
          data-testid={`${testid}-suggestions`}
        >
          {suggestions.length > 0 && (
            <div>
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-50/60">
                From Inventory
              </div>
              {suggestions.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickInventory(item)}
                  className="w-full px-3 py-2 text-left text-[12px] hover:bg-violet-50 flex items-center justify-between gap-2 border-b last:border-0"
                  data-testid={`${testid}-opt-${item.id}`}
                >
                  <span className="text-slate-800 font-medium truncate">
                    {item.product_name}
                    {item.quantity !== undefined && (
                      <span className="ml-1.5 text-[10px] mono text-slate-500 font-normal">
                        (avail: {item.quantity})
                      </span>
                    )}
                  </span>
                  {item.sku && <span className="mono text-[10px] text-slate-500 flex-shrink-0">{item.sku}</span>}
                </button>
              ))}
            </div>
          )}
          {canAddCustom && (
            <div>
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-50/60">
                Custom (not in Inventory)
              </div>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={acceptCustom}
                className="w-full px-3 py-2 text-left text-[12px] hover:bg-amber-50 flex items-center gap-2"
                data-testid={`${testid}-add-custom`}
              >
                <Plus size={12} className="text-amber-600" />
                <span className="text-slate-800">Use as custom: <span className="font-semibold">"{trimmed}"</span></span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
